import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getPerineumRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface PerineumRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
  isViewOnly: boolean;
}

interface PerineumItem {
  label: string;
  value: string | undefined;
  showOther?: boolean;
  otherValue?: string | undefined;
}

export default function PerineumRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  onRefresh,
  isViewOnly,
}: PerineumRowProps) {
  const { status, otherLaceration, repair, notes, hasAnswers, hasNotes } = useMemo(
    () =>
      observation
        ? getPerineumRowValues(observation)
        : {
            status: undefined,
            otherLaceration: undefined,
            repair: undefined,
            notes: undefined,
            hasAnswers: false,
            hasNotes: false,
          },
    [observation],
  );

  const perineumItems = useMemo<PerineumItem[]>(
    () => [
      { label: "Status", value: status },
      { label: "Other Laceration", value: otherLaceration },
      { label: "Repair", value: repair },
    ],
    [status, otherLaceration, repair],
  );

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Perineum:</TextWrapper>
            <List fz="sm" withPadding>
              {perineumItems.map(
                (item, index) =>
                  (item.value || (item.showOther && item.otherValue)) && (
                    <List.Item key={`${item.label}-${item.value || item.otherValue}`}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}:
                        </TextWrapper>
                        <TextWrapper size="sm">{item.value}</TextWrapper>
                      </Group>
                    </List.Item>
                  ),
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Perineum Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={PerineumRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
